// Winback sitesi → Ayarlar (sahip 2026-10-07): herkes KENDİ şifresini değiştirir (mevcut şifre
// şart); yönetici kullanıcı ekler, şifre belirler, rol/aktiflik değiştirir, siler. Sunucu kendini
// kilitlemeyi (kendini silme/pasifleştirme/yetki düşürme) ve son aktif yöneticinin gitmesini
// reddeder — burada da o kontroller gizlenir, ama otorite sunucudur (guardUserChange).

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { cardStyle, ghostBtnStyle, Icon, inputStyle, knobStyle, primaryBtnStyle, Select, Skeleton, switchStyle } from '../ui';
import { changeOwnPassword, createUser, deleteUser, listUsers, updateUser, type SiteRole, type SiteUser, type SiteUserRow } from './api';

const MIN_PW = 6;

const labelStyle: React.CSSProperties = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' };
const field: React.CSSProperties = { ...inputStyle, padding: '9px 12px', fontSize: '13px' };

function Notice({ kind, text }: { kind: 'ok' | 'err'; text: string }) {
  const color = kind === 'ok' ? '#37c46b' : '#ef5d5d';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color }}>
      <Icon name={kind === 'ok' ? 'check_circle' : 'error'} size={17} color={color} />
      <span>{text}</span>
    </div>
  );
}

function SectionHead({ icon, title, sub }: { icon: string; title: string; sub: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
      <Icon name={icon} size={19} color="var(--accent)" />
      <div>
        <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginTop: '1px' }}>{sub}</div>
      </div>
    </div>
  );
}

const errText = (e: unknown) => (e instanceof ApiError && e.message === 'network_error' ? 'Bağlantı hatası. Lütfen tekrar deneyin.' : 'İşlem başarısız oldu.');

function OwnPassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const submit = async () => {
    if (busy) return;
    if (next.length < MIN_PW) return setMsg({ kind: 'err', text: 'Şifre en az ' + MIN_PW + ' karakter olmalı' });
    if (next !== again) return setMsg({ kind: 'err', text: 'Yeni şifreler eşleşmiyor' });
    setBusy(true);
    setMsg(null);
    try {
      const r = await changeOwnPassword(current, next);
      if (r.ok) {
        setCurrent('');
        setNext('');
        setAgain('');
        setMsg({ kind: 'ok', text: 'Şifreniz değişti. Diğer cihazlardaki oturumlar kapatıldı.' });
      } else {
        setMsg({ kind: 'err', text: r.error || 'İşlem başarısız oldu.' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...cardStyle, overflow: 'hidden', marginBottom: '18px' }}>
      <SectionHead icon="key" title="Şifremi değiştir" sub="Mevcut şifreniz gerekir" />
      <form
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
        style={{ padding: '16px 18px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 180px' }}>
          <span style={labelStyle}>MEVCUT ŞİFRE</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={field} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 180px' }}>
          <span style={labelStyle}>YENİ ŞİFRE</span>
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={field} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 180px' }}>
          <span style={labelStyle}>YENİ ŞİFRE (TEKRAR)</span>
          <input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} style={field} />
        </label>
        <button type="submit" disabled={busy || !current || !next} style={{ ...primaryBtnStyle, padding: '10px 18px', opacity: busy || !current || !next ? 0.55 : 1 }}>
          {busy ? 'Kaydediliyor…' : 'Şifreyi değiştir'}
        </button>
        {msg && <div style={{ flexBasis: '100%' }}><Notice kind={msg.kind} text={msg.text} /></div>}
      </form>
    </div>
  );
}

function AddUser({ onAdded }: { onAdded: () => void }) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<SiteRole>('user');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await createUser({ username: username.trim(), displayName: displayName.trim(), password, role });
      if (r.ok) {
        setMsg({ kind: 'ok', text: (r.user?.username || username) + ' eklendi' });
        setUsername('');
        setDisplayName('');
        setPassword('');
        setRole('user');
        onAdded();
      } else {
        setMsg({ kind: 'err', text: r.error || 'İşlem başarısız oldu.' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); void submit(); }}
      style={{ padding: '16px 18px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)' }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 150px' }}>
        <span style={labelStyle}>KULLANICI ADI</span>
        <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" placeholder="ör. asya" style={field} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 150px' }}>
        <span style={labelStyle}>GÖRÜNEN AD</span>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="off" placeholder="isteğe bağlı" style={field} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 150px' }}>
        <span style={labelStyle}>ŞİFRE</span>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder={'en az ' + MIN_PW + ' karakter'} style={field} />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={labelStyle}>ROL</span>
        <Select compact value={role} onChange={(v) => setRole(v as SiteRole)} options={[{ value: 'user', label: 'Kullanıcı' }, { value: 'admin', label: 'Yönetici' }]} />
      </div>
      <button type="submit" disabled={busy || !username.trim() || !password} style={{ ...primaryBtnStyle, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: '6px', opacity: busy || !username.trim() || !password ? 0.55 : 1 }}>
        <Icon name="person_add" size={17} color="#fff" />
        <span>{busy ? 'Ekleniyor…' : 'Kullanıcı ekle'}</span>
      </button>
      {msg && <div style={{ flexBasis: '100%' }}><Notice kind={msg.kind} text={msg.text} /></div>}
    </form>
  );
}

const userCols = 'minmax(140px,1.2fr) minmax(120px,1fr) 130px 70px minmax(220px,1.4fr) 40px';

function UserRow({ row, me, onChanged, onError }: { row: SiteUserRow; me: SiteUser; onChanged: (msg: string) => void; onError: (msg: string) => void }) {
  const self = row.username === me.username;
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const patch = async (p: Parameters<typeof updateUser>[1], okText: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await updateUser(row.username, p);
      if (r.ok) onChanged(okText);
      else onError(r.error || 'İşlem başarısız oldu.');
    } catch (e) {
      onError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const setPassword = async () => {
    if (pw.length < MIN_PW) return onError('Şifre en az ' + MIN_PW + ' karakter olmalı');
    await patch({ password: pw }, row.username + ' için şifre belirlendi');
    setPw('');
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await deleteUser(row.username);
      if (r.ok) onChanged(row.username + ' silindi');
      else onError(r.error || 'İşlem başarısız oldu.');
    } catch (e) {
      onError(errText(e));
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: userCols, gap: '12px', padding: '10px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center', opacity: busy ? 0.6 : 1 }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {row.username}
          {self && <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.06em', padding: '2px 6px', borderRadius: '6px', background: 'var(--accent-soft)', color: 'var(--accent)' }}>SİZ</span>}
        </span>
      </span>
      <span style={{ fontSize: '12.5px', color: 'var(--dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.displayName}>{row.displayName}</span>
      {self ? (
        <span style={{ fontSize: '12.5px', color: 'var(--dim)' }}>{row.roleLabel}</span>
      ) : (
        <Select compact value={row.role} onChange={(v) => void patch({ role: v as SiteRole }, row.username + ' rolü güncellendi')} options={[{ value: 'user', label: 'Kullanıcı' }, { value: 'admin', label: 'Yönetici' }]} />
      )}
      <span title={row.active ? 'Aktif' : 'Pasif'}>
        {self ? (
          <span style={{ fontSize: '12px', color: '#37c46b' }}>Aktif</span>
        ) : (
          <span
            role="switch"
            aria-checked={row.active}
            tabIndex={0}
            onClick={() => void patch({ active: !row.active }, row.username + (row.active ? ' pasifleştirildi' : ' aktifleştirildi'))}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void patch({ active: !row.active }, row.username + (row.active ? ' pasifleştirildi' : ' aktifleştirildi')); } }}
            style={{ ...switchStyle(row.active), display: 'inline-block' }}
          >
            <span style={knobStyle(row.active)} />
          </span>
        )}
      </span>
      <form onSubmit={(e) => { e.preventDefault(); void setPassword(); }} style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" placeholder="Yeni şifre" style={{ ...field, padding: '7px 10px', fontSize: '12.5px', minWidth: 0 }} />
        <button type="submit" disabled={!pw || busy} style={{ ...ghostBtnStyle, padding: '7px 11px', fontSize: '12px', whiteSpace: 'nowrap', opacity: !pw || busy ? 0.55 : 1 }}>Şifre belirle</button>
      </form>
      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {!self && (confirmDelete ? (
          <span style={{ display: 'flex', gap: '4px' }}>
            <button type="button" onClick={() => void remove()} title="Silmeyi onayla" style={{ ...ghostBtnStyle, padding: '5px', color: '#ef5d5d', borderColor: 'rgba(239,93,93,.4)', display: 'flex' }}>
              <Icon name="check" size={16} color="#ef5d5d" />
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(true)} title="Sil" aria-label={row.username + ' sil'} style={{ ...ghostBtnStyle, padding: '5px', display: 'flex' }}>
            <Icon name="delete" size={16} color="var(--faint)" />
          </button>
        ))}
      </span>
    </div>
  );
}

function UserAdmin({ me }: { me: SiteUser }) {
  const [rows, setRows] = useState<SiteUserRow[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(() => {
    listUsers()
      .then((r) => { setRows(r); setLoadError(''); })
      .catch((e) => setLoadError(errText(e)));
  }, []);
  useEffect(load, [load]);

  const changed = (text: string) => { setMsg({ kind: 'ok', text }); load(); };
  const failed = (text: string) => { setMsg({ kind: 'err', text }); load(); };

  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <SectionHead icon="group" title="Kullanıcılar" sub="Bu sitenin hesapları — Call Agent panelinden ayrıdır" />
      {msg && <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)' }}><Notice kind={msg.kind} text={msg.text} /></div>}
      {loadError ? (
        <div style={{ padding: '16px 18px' }}><Notice kind="err" text={loadError} /></div>
      ) : !rows ? (
        <div style={{ padding: '16px 18px' }}><Skeleton heights={[40, 40, 40]} gap={8} /></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: '780px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: userCols, gap: '12px', padding: '8px 18px', borderBottom: '1px solid var(--border)', ...labelStyle, fontSize: '10px' }}>
              <span>KULLANICI ADI</span>
              <span>GÖRÜNEN AD</span>
              <span>ROL</span>
              <span>AKTİF</span>
              <span>ŞİFRE</span>
              <span />
            </div>
            {rows.map((r) => (
              <UserRow key={r.username} row={r} me={me} onChanged={changed} onError={failed} />
            ))}
          </div>
        </div>
      )}
      <AddUser onAdded={load} />
    </div>
  );
}

export default function SiteSettings({ user }: { user: SiteUser }) {
  return (
    <div style={{ maxWidth: '1100px' }}>
      <OwnPassword />
      {user.role === 'admin' && <UserAdmin me={user} />}
    </div>
  );
}
